// MUI components
import { useState, useContext, useEffect } from 'react'
import { IntakeContext } from '../context/IntakeContext'
import axios from 'axios'

import { toast } from 'react-toastify'
import { selectData } from 'layouts/onboarding'

import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import { Link, Button } from '@mui/material'

import { format, subYears, differenceInYears } from 'date-fns'
import { MuiSplitDateField } from './FormField/MuiSplitDateField'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'
import zipState from 'zip-state'
import { getApiUrl } from 'utils/api/getApiUrl'

const OnboardingFirstLastPhoneDOB = ({
  fieldErrors,
  setFieldErrors,
  onFieldFocus,
}) => {
  const { intakeState, updateIntakeStateAtKey, updateUser } =
    useContext(IntakeContext)
  const [ageQuestion, setAgeQuestion] = useState(true)

  const dateToYears = intakeState.dateOfBirth
    ? differenceInYears(new Date(), new Date(intakeState.dateOfBirth))
    : ''
  const yearsToDate = (years) =>
    format(subYears(new Date(), years), 'yyyy-MM-dd')

  const [isMobile, setIsMobile] = useState(window.innerWidth < 600)

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600)
    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // add an onSubmit button and call the context function `updateUser`
  // const onSubmit = () => updateUser()
  const onSubmit = () => {
    updateUser()
    toast.info('You have successfully updated your user profile', {
      position: 'top-center',
      autoClose: 5000,
      pauseOnFocusLoss: false,
      pauseOnHover: false,
    })
  }

  const [localForm, setLocalForm] = useState({
    firstName: intakeState.firstName || '',
    lastName: intakeState.lastName || '',
    phoneNumber: intakeState.phoneNUmber | '',
  })
  const handleChange = (field) => (value) => {
    setLocalForm((prev) => ({ ...prev, [field]: value }))
  }

  const checkUserExists = async ({ email, phone }) => {
    const apiUrl = getApiUrl()
    try {
      const res = await axios.post(`${apiUrl}/api/checkUserExists`, {
        email,
        phone,
      })
      return res.data
    } catch (err) {
      console.error('User check failed', err)
      return { exists: false }
    }
  }

  const handleEmailBlur = async (email) => {
    if (!email) return
    //possibly standardize lowercase for check
    const lowercasedEmail = email.toLowerCase()
    const { exists, conflict } = await checkUserExists({ email })
    if (exists && conflict === 'email') {
      setFieldErrors((prev) => ({
        ...prev,
        email: true,
      }))
    } else {
      setFieldErrors((prev) => ({ ...prev, email: false }))
    }
  }

  const handlePhoneBlur = async (rawPhone) => {
    let phone = rawPhone.replace(/\D/g, '')

    if (phone.length === 11 && phone.startsWith('1')) {
      phone = phone.slice(1)
    }

    if (phone.length !== 10) return

    const { exists, conflict } = await checkUserExists({ phone })
    if (exists && conflict === 'phone') {
      setFieldErrors((prev) => ({
        ...prev,
        phone: true,
      }))
    } else {
      setFieldErrors((prev) => ({ ...prev, phone: false }))
    }
  }
  // console.log(intakeState)

  return (
    <Box sx={{ textAlign: 'left' }}>
      {/* {isMobile ? (
        <></>
      ) : (
        <>
          {window.location.pathname !== '/fork-flow' &&
          window.location.pathname !== '/dashboard' ? (
            <Box mb={0.75}>
              <Typography variant="body2">
                Provide your information to get your Free Federal Retirement
                Report
              </Typography>
            </Box>
          ) : (
            <></>
          )}
        </>
      )} */}

      <Box mb={1}>
        <Typography variant="h5">
          Let&apos;s start with the basic information
        </Typography>
      </Box>

      <Box component="form" py={3}>
        <Grid container>
          <Typography variant="body1Medium" mb={2}>
            Name
          </Typography>
          <Grid container columnSpacing={3}>
            <Grid item xs={12} mb={2}>
              {renderMuiFormField({
                showAdornment: true,
                required: true,
                inputType: 'text',
                id: 'firstName',
                label: 'First Name',
                value: intakeState.firstName,
                fullWidth: true,
                enableHelpText: true,
                helpText: 'So we know what to call you',
                onChange: (firstName) => {
                  updateIntakeStateAtKey({
                    firstName,
                    fullName: `${firstName} ${intakeState?.lastName || ''}`,
                  })
                },
                onBlur: (firstName) => {
                  updateIntakeStateAtKey({
                    firstName,
                    fullName: `${firstName} ${intakeState?.lastName || ''}`,
                  })
                },
              })}
            </Grid>
            <Grid item xs={12} mb={2}>
              {renderMuiFormField({
                required: true,
                showAdornment: true,
                inputType: 'text',
                id: 'lastName',
                label: 'Last Name',
                value: intakeState.lastName,
                fullWidth: true,
                enableHelpText: true,
                helpText: 'So we know what to call you',

                onChange: (lastName) => {
                  updateIntakeStateAtKey({
                    lastName,
                    fullName: `${intakeState?.firstName || ''} ${lastName}`,
                  })
                },
                onBlur: (lastName) => {
                  updateIntakeStateAtKey({
                    lastName,
                    fullName: `${intakeState?.firstName || ''} ${lastName}`,
                  })
                },
              })}
            </Grid>
          </Grid>
          <Typography variant="body1Medium" mb={2}>
            Contact Info
          </Typography>
          <Grid item xs={12} mb={2}>
            {renderMuiFormField({
              required: true,
              showAdornment: true,
              inputType: 'tel',
              id: 'phone',
              label: 'Phone Number',
              value: intakeState.phone,
              fullWidth: true,
              enableHelpText: true,
              helpText:
                'A mobile or home phone number, whichever you are more likely to answer.',

              forceError: fieldErrors.phone,
              onBlur: (val) => {
                handlePhoneBlur(val)
              },
              onChange: (phone) => {
                updateIntakeStateAtKey({
                  phone,
                })
              },
            })}

            {fieldErrors.phone && (
              <Typography
                sx={{
                  color: 'red',
                  '& a': {
                    fontWeight: 600,
                    color: '#344767 !important',
                    textDecoration: 'underline',
                  },
                }}
                variant="inputNote"
                component="p"
              >
                Phone number is already in use. Please{' '}
                <Link href="/dashboard">Sign in</Link> instead.
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} mb={2}>
            {renderMuiFormField({
              required: true,
              showAdornment: true,
              inputType: 'email',
              id: 'email',
              label: 'Email Address',
              value: intakeState.email,
              enableHelpText: true,
              helpText:
                'Work or personal email, whichever you use most frequently.',
              fullWidth: true,
              forceError: fieldErrors.email,
              onBlur: (val) => {
                handleEmailBlur(val)
              },
              onChange: (email) => {
                updateIntakeStateAtKey({
                  email,
                })
              },
            })}
            {fieldErrors.email && (
              <Typography
                sx={{
                  color: 'red',
                  '& a': {
                    fontWeight: 600,
                    color: '#344767 !important',
                    textDecoration: 'underline',
                  },
                }}
                variant="inputNote"
                component="p"
              >
                Email address is already in use. Please{' '}
                <Link href="/dashboard">Sign in</Link> instead.
              </Typography>
            )}
          </Grid>

          <Grid item xs={12} mb={2}>
            {renderMuiFormField({
              required: true,
              showAdornment: true,
              inputType: 'zipcode',
              id: 'zipCode',
              label: 'Zip Code',
              value: intakeState.zipcode,
              enableHelpText: true,
              fullWidth: true,
              onChange: (zipcode) => {
                updateIntakeStateAtKey({
                  zipcode: zipcode,
                  state:
                    selectData.states.find((s) => s.abbr === zipState(zipcode))
                      ?.value || null,
                })
              },
            })}
          </Grid>

          <Typography variant="body1Medium" mb={2}>
            Date Of Birth
          </Typography>
          {ageQuestion ? (
            <>
              <Grid container columnSpacing={1}>
                <Grid item xs={12}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-end"
                    height="100%"
                  >
                    <MuiSplitDateField
                      id="dob"
                      required
                      label=""
                      value={intakeState.dateOfBirth}
                      onChange={(date) => {
                        updateIntakeStateAtKey({ dateOfBirth: date })
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-start">
                  <Button
                    variant="text"
                    color="info"
                    sx={{ height: '100%' }}
                    onClick={() => setAgeQuestion(false)}
                  >
                    Use my age instead
                  </Button>
                </Box>
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12}>
                {renderMuiFormField({
                  showAdornment: true,
                  required: true,
                  inputType: 'int',
                  id: 'age',
                  label: 'Age',
                  value: dateToYears,
                  enableHelpText: true,
                  onChange: (e) => {
                    updateIntakeStateAtKey({
                      dateOfBirth: yearsToDate(e),
                    })
                  },
                })}
              </Grid>
              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-start">
                  <Button
                    variant="text"
                    color="info"
                    sx={{ height: '100%' }}
                    onClick={() => setAgeQuestion(true)}
                  >
                    Use my date of birth instead
                  </Button>
                </Box>
              </Grid>
            </>
          )}
        </Grid>
      </Box>
    </Box>
  )
}
OnboardingFirstLastPhoneDOB.isInvalid = ({
  firstName,
  lastName,
  phone,
  email,
  zipcode,
  fieldErrors = {},
}) => {
  return (
    (firstName?.length || 0) === 0 ||
    (lastName?.length || 0) === 0 ||
    !/\b\d{10}\b/.test(phone) ||
    (zipcode?.length || 0) !== 5 ||
    !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email) ||
    fieldErrors.email ||
    fieldErrors.phone
  )
}
export default OnboardingFirstLastPhoneDOB
